import React, { useState, useCallback, useEffect, useRef } from "react";
import { normalizeTemplateBody } from "../../../../../utils/templateBodyValidation";
import { useForm, useFieldArray } from "react-hook-form";
import { toast } from "react-toastify";
import { TEMPLATE_BUTTON_TYPE, TEMPLATE_CATEGORY } from "../constants/template";
import { templateSchema } from "../shemas/template";
import { useTemplateNameAvailability } from "../hooks/use-template-name-availability";
import { TEMPLATE_NAME_DELETED_MESSAGE } from "../helpers/template-name-availability";
import useTemplateMediaSelection, {
  TEMPLATE_MEDIA_STATUS,
} from "../../../../../hooks/useTemplateMediaSelection";
import { SelectTagWidgetContainer } from "../../BroadcastView.styles";
import {
  AddButtonAction,
  BroadcastAppCard,
  ButtonInputRow,
  ButtonListContainer,
  Checkbox,
  CheckboxContainer,
  CheckboxLabel,
  Container,
  ContainerActions,
  DropZoneContainer,
  DropZoneDescription,
  DropZoneText,
  FileInput,
  FileName,
  FileSize,
  FileTypeIcon,
  FieldAdornment,
  FieldCheckIcon,
  FieldErrorIcon,
  FieldSpinner,
  FieldWrapper,
  FormContainer,
  FormInput,
  FormInputContainer,
  FormInputDescription,
  FormInputError,
  FormInputLabel,
  ListContainer,
  ListContent,
  ListContentItem,
  ListTitle,
  NameStatusText,
  PreviewContainer,
  PreviewContent,
  PreviewHeader,
  PreviewInfo,
  PreviewMedia,
  PreviewTitle,
  RemoveButtonIcon,
  ReplaceButton,
  Select,
} from "../styles";
import WhatsAppBodyEditor from "./whatsapp-body-editor/WhatsAppBodyEditor.component";
import { FaPlus, FaRegTrashAlt } from "react-icons/fa";
import { BackLink } from "../../Campaigns.style";
import Button from "../../../../../components/shared/components/button/Button.style";
import { WhatsAppPreview } from "./whatsapp-preview";

export const TemplateForm = ({
  handleNext,
  handleBack,
  updateBroadcastData,
  broadcastData,
  handleSkip,
}) => {
  const bodyEditorRef = useRef(null);
  const normalizedBodyRef = useRef(null);
  const [TextWithFormat, setTextWithFormat] = useState("");
  const [variableExamples, setVariableExamples] = useState(
    broadcastData?.templateVariables || [],
  );

  const { isChecking, isAvailable, isTaken, isDeletedName, onNameChange } =
    useTemplateNameAvailability();

  const {
    register,
    handleSubmit,
    errors,
    setValue,
    watch,
    control,
    triggerValidation,
  } = useForm({
    validationSchema: templateSchema,
    defaultValues: {
      // template
      templateName: broadcastData?.templateName || "",
      templateCategory:
        broadcastData?.templateCategory || TEMPLATE_CATEGORY.MARKETING,

      // template message
      bodyMessage: broadcastData?.bodyMessage || "",
      templateVariables: broadcastData?.templateVariables || [],
      footerMessage: broadcastData?.footerMessage || "",

      // template file
      isAddMultimedia: broadcastData?.isAddMultimedia || false,

      // buttons
      buttons: broadcastData?.buttons || [],
    },
    mode: "onChange",
  });

  const buttons = useFieldArray({
    control: control,
    name: "buttons",
  });

  const {
    status: mediaStatus,
    sample: mediaSample,
    previewUrl,
    error: mediaError,
    select: selectMedia,
    clear: clearMedia,
  } = useTemplateMediaSelection();

  const file = mediaSample?.file || null;

  const fileTypeDisplayName = !file
    ? ""
    : file.type.startsWith("image/")
      ? "Imagen"
      : file.type.startsWith("video/")
        ? "Video"
        : "Documento";

  const formattedFileSize = (() => {
    if (!file?.size) return "0 Bytes";
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const k = 1024;
    const i = Math.floor(Math.log(file.size) / Math.log(k));
    return `${parseFloat((file.size / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  })();

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = "";
    if (selectedFile) selectMedia(selectedFile);
  };

  const sanitizeTemplateName = useCallback((name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "_")
      .replace(/_+/g, "_");
  }, []);

  const onSubmit = (data) => {
    if (isTaken) {
      toast.error("El nombre de la plantilla ya existe, elige otro.");
      return;
    }

    if (isDeletedName) {
      toast.error(TEMPLATE_NAME_DELETED_MESSAGE);
      return;
    }

    // F3: verify current validity before progressing — pending/invalid
    // selections must not be saved as the current media.
    if (data.isAddMultimedia && mediaStatus !== TEMPLATE_MEDIA_STATUS.VALID) {
      toast.error(
        mediaError?.message ||
          "Selecciona un archivo válido antes de continuar.",
      );
      return;
    }

    updateBroadcastData({
      templateName: data.templateName,
      templateCategory: data.templateCategory,
      bodyMessage: normalizeTemplateBody(data.bodyMessage),
      footerMessage: data.footerMessage?.trim(),
      isAddMultimedia: data.isAddMultimedia,
      templateFile: data.isAddMultimedia ? file : null,
      // This form always creates a brand-new template: clear any stale
      // templateId/templateFileUrl left over from a prior template-select
      // choice so a fresh local selection can never be bypassed (F3).
      templateId: null,
      templateFileUrl: null,
      bodyMessageWithFormat: normalizedBodyRef.current?.html ?? TextWithFormat,
      templateVariables: variableExamples,
      buttons: data.buttons,
    });

    if (!data.buttons || data.buttons?.length === 0) {
      handleSkip();
      return;
    }

    handleNext();
  };

  const handleContinue = (event) => {
    normalizedBodyRef.current = bodyEditorRef.current?.normalize();
    return handleSubmit(onSubmit)(event);
  };

  const handleOnBack = () => {
    clearMedia();
    updateBroadcastData({
      templateName: "",
      templateCategory: TEMPLATE_CATEGORY.MARKETING,
      bodyMessage: "",
      footerMessage: "",
      isAddMultimedia: false,
      templateFile: null,
      templateId: null,
      templateFileUrl: null,
      bodyMessageWithFormat: "",
      templateVariables: [],
      buttons: [],
    });
    handleBack();
  };

  useEffect(() => {
    register({ name: "bodyMessage" });
    register({ name: "templateVariables" });
    register({ name: "bodyText" });
  }, [register]);

  const isAddMultimedia = watch("isAddMultimedia");
  const footerMessage = watch("footerMessage");
  const buttonsFields = watch("buttons");

  return (
    <SelectTagWidgetContainer>
      <BroadcastAppCard>
        <Container>
          <FormContainer onSubmit={handleContinue}>
            <FormInputContainer>
              <FormInputLabel htmlFor="templateName">
                Nombre de la plantilla
              </FormInputLabel>

              <FieldWrapper>
                <FormInput
                  name="templateName"
                  type="text"
                  placeholder="Nombre de la plantilla"
                  style={{ paddingRight: "2.5rem" }}
                  onChange={(e) => {
                    const sanitized = sanitizeTemplateName(e.target.value);
                    setValue("templateName", sanitized);
                    onNameChange(sanitized);
                  }}
                  ref={register}
                />

                <FieldAdornment>
                  {isChecking && <FieldSpinner />}
                  {!isChecking && isAvailable && <FieldCheckIcon />}
                  {!isChecking && (isTaken || isDeletedName) && (
                    <FieldErrorIcon />
                  )}
                </FieldAdornment>
              </FieldWrapper>

              {isChecking && (
                <NameStatusText $status="checking">
                  Verificando disponibilidad…
                </NameStatusText>
              )}
              {!isChecking && isAvailable && (
                <NameStatusText $status="available">
                  ✓ Nombre disponible
                </NameStatusText>
              )}
              {!isChecking && isTaken && (
                <NameStatusText $status="taken">
                  Este nombre ya está en uso, elige otro.
                </NameStatusText>
              )}
              {!isChecking && isDeletedName && (
                <NameStatusText $status="taken">
                  {TEMPLATE_NAME_DELETED_MESSAGE}
                </NameStatusText>
              )}

              {errors.templateName && (
                <FormInputError>{errors.templateName.message}</FormInputError>
              )}
            </FormInputContainer>

            <FormInputContainer>
              <FormInputLabel htmlFor="bodyMessage">
                Cuerpo del Mensaje
              </FormInputLabel>

              <WhatsAppBodyEditor
                bodyEditorRef={bodyEditorRef}
                initialText={broadcastData?.bodyMessage}
                initialHtml={broadcastData?.bodyMessageWithFormat}
                initialVariables={broadcastData?.templateVariables}
                onChangeMarkdown={(markdown) => {
                  // Validated in onChangeText, together with the examples.
                  setValue("bodyMessage", markdown);
                }}
                onChangeHtml={(html) => {
                  setTextWithFormat(html);
                }}
                onChangeVariables={(variables) => {
                  setVariableExamples(variables);
                  setValue("templateVariables", variables);
                }}
                onChangeText={(text) => {
                  // The editor emits the markdown, the variables and, last, the
                  // visible text, on every change. The body rule needs all
                  // three (the visible text is where braces typed by hand
                  // show), so it is validated once, here, with the fresh set.
                  setValue("bodyText", text);
                  triggerValidation("bodyMessage");
                }}
                error={!!errors.bodyMessage}
                errorMessage={errors.bodyMessage?.message}
              />

              <FormInputDescription>
                Usa la barra para dar formato. Selecciona texto y haz clic en
                "Crear campo" para agregar variables personalizadas (ej: nombre,
                producto).
              </FormInputDescription>
            </FormInputContainer>

            <FormInputContainer>
              <FormInputLabel htmlFor="footerMessage">
                Pie de mensaje (opcional)
              </FormInputLabel>

              <FormInput
                name="footerMessage"
                type="text"
                placeholder="Ej. Saludos, equipo de ventas"
                ref={register}
              />

              {errors.footerMessage && (
                <FormInputError>{errors.footerMessage.message}</FormInputError>
              )}
            </FormInputContainer>

            <FormInputContainer>
              <FormInputLabel htmlFor="templateCategory">
                Categoría de la plantilla
              </FormInputLabel>

              <Select
                id="templateCategory"
                name="templateCategory"
                ref={register}
              >
                <option value={TEMPLATE_CATEGORY.MARKETING}>
                  Comercial (recomendado)
                </option>

                <option value={TEMPLATE_CATEGORY.UTILITY}>
                  Avisos Generales
                </option>
              </Select>

              {errors.templateCategory && (
                <FormInputError>
                  {errors.templateCategory.message}
                </FormInputError>
              )}

              <FormInputDescription>
                Meta puede reclasificar tu plantilla si detecta contenido
                comercial, incluso si la categorizas como "Avisos Generales".
                Asegúrate de categorizarla correctamente para evitar rechazos o
                cambios inesperados.
              </FormInputDescription>
            </FormInputContainer>

            <FormInputContainer>
              <CheckboxContainer>
                <Checkbox
                  type="checkbox"
                  id="isAddMultimedia"
                  name="isAddMultimedia"
                  ref={register}
                  onChange={(e) => {
                    // F3: media-off clears immediately, not only on a
                    // separate watch effect.
                    if (!e.target.checked) clearMedia();
                  }}
                />

                <CheckboxLabel htmlFor="isAddMultimedia">
                  Agregar multimedia (imagen, video o documento)
                </CheckboxLabel>
              </CheckboxContainer>
            </FormInputContainer>

            {isAddMultimedia && (
              <>
                <FormInputContainer>
                  {mediaStatus !== TEMPLATE_MEDIA_STATUS.VALID ? (
                    <DropZoneContainer>
                      <DropZoneText>Sube tu archivo o haz clic</DropZoneText>

                      <DropZoneDescription>
                        JPEG y PNG: hasta 5 MB. MP4: hasta 16 MB. PDF: hasta 100
                        MB.
                      </DropZoneDescription>

                      <FileInput
                        type="file"
                        id="file"
                        name="file"
                        aria-label="Selecciona un archivo"
                        accept=".jpg,.jpeg,.png,.mp4,.pdf"
                        multiple={false}
                        onChange={handleFileChange}
                        disabled={mediaStatus === TEMPLATE_MEDIA_STATUS.PENDING}
                      />

                      {mediaStatus === TEMPLATE_MEDIA_STATUS.PENDING && (
                        <FormInputDescription>
                          Validando archivo...
                        </FormInputDescription>
                      )}
                      {mediaStatus === TEMPLATE_MEDIA_STATUS.INVALID &&
                        mediaError && (
                          <FormInputError>{mediaError.message}</FormInputError>
                        )}
                    </DropZoneContainer>
                  ) : (
                    <PreviewContainer>
                      <PreviewHeader>
                        <PreviewTitle>Archivo seleccionado</PreviewTitle>

                        <ReplaceButton type="button" onClick={clearMedia}>
                          Eliminar / Reemplazar
                        </ReplaceButton>
                      </PreviewHeader>

                      <PreviewContent>
                        <PreviewMedia>
                          {file.type.startsWith("image/") ? (
                            <img src={previewUrl} alt="Preview" />
                          ) : file.type.startsWith("video/") ? (
                            <video src={previewUrl} />
                          ) : (
                            <FileTypeIcon>
                              <i className="fas fa-file-pdf" />
                            </FileTypeIcon>
                          )}
                        </PreviewMedia>

                        <PreviewInfo>
                          <FileName>{file.name}</FileName>

                          <FileSize>
                            {formattedFileSize} • {fileTypeDisplayName}
                          </FileSize>
                        </PreviewInfo>
                      </PreviewContent>
                    </PreviewContainer>
                  )}
                </FormInputContainer>
              </>
            )}

            <FormInputContainer>
              <FormInputLabel htmlFor="buttons">
                Botones (Opcional)
              </FormInputLabel>

              <ButtonListContainer>
                {buttons.fields.map((button, index) => (
                  <ButtonInputRow key={button.id}>
                    <FormInput
                      name={`buttons[${index}].text`}
                      type="text"
                      placeholder={`Texto del botón ${index + 1}`}
                      defaultValue={button.text}
                      ref={register}
                      maxLength={25}
                      onChange={() => triggerValidation("buttons")}
                    />
                    <input
                      type="hidden"
                      name={`buttons[${index}].type`}
                      defaultValue={button.type}
                      ref={register}
                    />

                    <RemoveButtonIcon
                      type="button"
                      onClick={() => buttons.remove(index)}
                      title="Eliminar botón"
                    >
                      <FaRegTrashAlt />
                    </RemoveButtonIcon>
                  </ButtonInputRow>
                ))}

                {buttons.fields.length < 3 && (
                  <AddButtonAction
                    type="button"
                    onClick={() =>
                      buttons.append({
                        text: "",
                        type: TEMPLATE_BUTTON_TYPE.QUICK_REPLY,
                      })
                    }
                  >
                    <FaPlus /> Agregar botón
                  </AddButtonAction>
                )}
              </ButtonListContainer>

              <FormInputDescription>
                Máximo 3 botones. Cada botón puede tener hasta 25 caracteres.
              </FormInputDescription>

              {errors.buttons && (
                <FormInputError>{errors.buttons.message}</FormInputError>
              )}

              {buttons.fields.map(
                (button, index) =>
                  errors.buttons &&
                  errors.buttons[index] && (
                    <FormInputError key={index}>
                      Botón {index + 1}:{" "}
                      {errors.buttons[index].text &&
                        errors.buttons[index].text.message}
                    </FormInputError>
                  ),
              )}
            </FormInputContainer>
          </FormContainer>

          <div
            style={{
              display: "grid",
              gap: "1rem",
            }}
          >
            <WhatsAppPreview
              file={file}
              previewUrl={previewUrl}
              textWithFormat={TextWithFormat}
              footerMessage={footerMessage}
              buttons={buttonsFields}
            />

            <ListContainer>
              <ListTitle>Consejos</ListTitle>

              <ListContent>
                <ListContentItem>
                  Usa variables para personalizar mensajes (ej: nombre del
                  cliente).
                </ListContentItem>

                <ListContentItem>
                  El mensaje no puede iniciar ni terminar con un campo (los
                  signos . ! ? y las negritas o cursivas no cuentan como texto).
                </ListContentItem>

                <ListContentItem>
                  Si usas multimedia, que refuerce el mensaje (no lo reemplace).
                </ListContentItem>

                <ListContentItem>
                  1–2 botones suelen convertir mejor que 3.
                </ListContentItem>

                <ListContentItem>
                  El pie funciona para "Deja de recibir" o info legal.
                </ListContentItem>
              </ListContent>
            </ListContainer>
          </div>
        </Container>

        <ContainerActions>
          <BackLink onClick={handleOnBack}>← Atrás</BackLink>

          <Button
            designV4
            type="submit"
            disabled={isTaken || isDeletedName || isChecking}
            onClick={handleContinue}
          >
            Continuar
          </Button>
        </ContainerActions>
      </BroadcastAppCard>
    </SelectTagWidgetContainer>
  );
};
